
import React, { useState, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Heart } from 'lucide-react';
import DiscoveryView from './match/DiscoveryView';
import MatchesView from './match/MatchesView';
import UpgradePromo from './match/UpgradePromo';
import { useMatchProfiles } from './match/useMatchProfiles';
import LoadingState from './match/LoadingState';
import { matchedProfiles } from './match/mockData';

interface MusiciansMatchProps {
  mode?: 'discovery' | 'matches';
}

const MusiciansMatchSystem: React.FC<MusiciansMatchProps> = ({ mode = 'discovery' }) => {
  const [activeTab, setActiveTab] = useState<"discovery" | "matches">(mode);
  const { 
    profiles, 
    currentProfile, 
    currentProfileIndex, 
    isLoading, 
    swipeKey,
    handleLike, 
    handlePass 
  } = useMatchProfiles();

  // Tab change handler with useCallback to prevent unnecessary rerenders
  const handleTabChange = useCallback((value: string) => {
    console.log(`Changing tab from ${activeTab} to ${value}`);
    setActiveTab(value as "discovery" | "matches");
  }, [activeTab]);
  
  if (isLoading) {
    return <LoadingState />;
  }
  
  return (
    <div className="space-y-6">
      <Tabs 
        defaultValue={activeTab} 
        value={activeTab}
        onValueChange={handleTabChange} 
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="discovery" className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>Discover Musicians</span>
          </TabsTrigger>
          <TabsTrigger value="matches" className="flex items-center gap-1">
            <Heart className="h-4 w-4" />
            <span>Your Matches</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Only render the active tab content to prevent issues with inactive components */}
        {activeTab === "discovery" && (
          <TabsContent value="discovery" className="mt-6">
            <DiscoveryView 
              currentProfile={currentProfile} 
              currentProfileIndex={currentProfileIndex}
              profiles={profiles}
              onLike={handleLike}
              onPass={handlePass}
              swipeKey={swipeKey}
            />
          </TabsContent>
        )}
        
        {activeTab === "matches" && (
          <TabsContent value="matches" className="mt-6">
            <MatchesView 
              matchedProfiles={matchedProfiles}
              onSwitchToDiscovery={() => setActiveTab('discovery')}
            />
          </TabsContent>
        )}
      </Tabs>
      
      <UpgradePromo profiles={profiles} />
    </div>
  );
};

export default MusiciansMatchSystem;
